import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export interface OutboundWebhook {
  id: string;
  name: string;
  url: string;
  eventTypes: string[];
  active: boolean;
  createdAt: string;
  updatedAt: string;
  recentDeliveries?: {
    success: number;
    failed: number;
    lastDeliveryAt: string | null;
  };
}

export interface CreateWebhookResult extends OutboundWebhook {
  secret: string;
}

export function useOutboundWebhooks() {
  return useQuery({
    queryKey: ['outbound-webhooks'],
    queryFn: () => api.get<{ data: OutboundWebhook[] }>('/outbound-webhooks'),
  });
}

export function useWebhookEventTypes() {
  return useQuery({
    queryKey: ['outbound-webhooks', 'event-types'],
    queryFn: () => api.get<{ data: string[] }>('/outbound-webhooks/event-types'),
    staleTime: 60 * 60 * 1000, // Static; reload once an hour at most
  });
}

export function useCreateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; url: string; eventTypes: string[] }) =>
      api.post<CreateWebhookResult>('/outbound-webhooks', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outbound-webhooks'] }),
  });
}

export function useUpdateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<{ name: string; url: string; eventTypes: string[]; active: boolean }> }) =>
      api.patch<void>(`/outbound-webhooks/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outbound-webhooks'] }),
  });
}

export function useDeleteWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/outbound-webhooks/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outbound-webhooks'] }),
  });
}

export function useTestWebhook() {
  return useMutation({
    mutationFn: (id: string) => api.post<{ deliveryId: string }>(`/outbound-webhooks/${id}/test`),
  });
}

export interface WebhookDelivery {
  id: string;
  webhookId: string;
  eventType: string;
  status: 'pending' | 'success' | 'failed';
  responseStatus: number | null;
  responseBody: string | null;
  errorMsg: string | null;
  attempts: number;
  firstTriedAt: string | null;
  lastTriedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export function useWebhookDeliveries(opts: { webhookId?: string; page: number; pageSize: number }) {
  return useQuery({
    queryKey: ['outbound-webhooks', 'deliveries', opts],
    queryFn: () =>
      api.get<{ data: WebhookDelivery[]; total: number; page: number; pageSize: number }>('/outbound-webhooks/deliveries', {
        query: {
          page: opts.page,
          pageSize: opts.pageSize,
          webhookId: opts.webhookId,
        },
      }),
    placeholderData: (prev) => prev,
  });
}
